import type { CoasterPlatformAdapter } from './adapter';
import type { BunchaWindowApi, StorageLike } from './contracts';
import {
  createLocalPhase4QaPlatformAdapter,
  LocalPhase4QaPlatformAdapter,
  type Phase4QaIdentityId,
} from './local-phase4-qa';
import { createRemotePhase4PlatformAdapter } from './remote-phase4';
import { localQaStorageKey, readLocalQaUrlContext } from './urls';

export interface Phase4PlatformFactoryOptions {
  apiBaseUrl: string;
  buncha?: BunchaWindowApi;
  fetch?: typeof fetch;
  timeoutMs?: number;
  search?: string;
  allowLocalQa?: boolean;
  storage?: StorageLike;
}

/**
 * Selects local emulation only when both the caller explicitly permits it and
 * the URL explicitly requests it. A production bundle cannot silently fall
 * back to fixture identities or local ownership semantics.
 */
export function createPhase4PlatformAdapter(
  options: Phase4PlatformFactoryOptions,
): CoasterPlatformAdapter {
  const search = new URLSearchParams(
    options.search ?? (typeof window === 'undefined' ? '' : window.location.search),
  );
  const localQa = readLocalQaUrlContext(`?${search.toString()}`);
  if (options.allowLocalQa === true && localQa) {
    const storage =
      options.storage ?? (typeof window === 'undefined' ? undefined : window.localStorage);
    if (!storage)
      throw new Error('The local Phase 4 QA adapter requires an explicit storage provider.');
    const selected: Phase4QaIdentityId = localQa.viewer;
    return createLocalPhase4QaPlatformAdapter({
      storage,
      identity: selected,
      storageKey: localQaStorageKey(localQa.session),
    });
  }
  return createRemotePhase4PlatformAdapter(options);
}

export function isLocalPhase4QaPlatformAdapter(
  adapter: CoasterPlatformAdapter,
): adapter is LocalPhase4QaPlatformAdapter {
  return adapter instanceof LocalPhase4QaPlatformAdapter;
}
